/*
 * Copyright (c) 2026 Gabriele Bonora. Tutti i diritti riservati.
 *
 * Software proprietario. Nessuna licenza d'uso, copia, modifica o distribuzione
 * e' concessa in assenza di autorizzazione scritta del titolare dei diritti.
 * Vedere il file LICENSE nella radice del progetto.
 */

/*
 * Registra un messaggio vocale dal microfono, con anteprima prima di tenerlo.
 *
 * MediaRecorder sceglie il formato da solo: webm/opus su Chrome e Firefox, mp4
 * su Safari. Il server accetta entrambi. Il browser chiede il permesso del
 * microfono solo sulle pagine in https (o su localhost).
 *
 * `onPronto(blob, nome, secondi)` riceve la registrazione confermata.
 */
import { useEffect, useRef, useState } from 'react';
import { Mic, Square, Check, Trash2 } from 'lucide-react';

const MAX_SECONDI = 300; // cinque minuti: oltre, conviene scrivere

function fmt(sec) {
  return `${Math.floor(sec / 60)}:${String(sec % 60).padStart(2, '0')}`;
}

export default function RegistratoreAudio({ onPronto, disabilitato }) {
  const [stato, setStato] = useState('fermo'); // fermo | registra | anteprima
  const [secondi, setSecondi] = useState(0);
  const [errore, setErrore] = useState('');
  const [anteprima, setAnteprima] = useState(null); // { blob, url, secondi }
  const recorder = useRef(null);
  const pezzi = useRef([]);
  const timer = useRef(null);
  const stream = useRef(null);
  const inizio = useRef(0);

  // Alla chiusura del componente: microfono spento e URL liberati.
  useEffect(() => () => {
    clearInterval(timer.current);
    stream.current?.getTracks().forEach((t) => t.stop());
  }, []);
  useEffect(() => () => { if (anteprima) URL.revokeObjectURL(anteprima.url); }, [anteprima]);

  const avvia = async () => {
    setErrore('');
    if (!navigator.mediaDevices?.getUserMedia || typeof window.MediaRecorder === 'undefined') {
      setErrore('Questo browser non permette di registrare audio.');
      return;
    }
    try {
      stream.current = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch {
      setErrore('Microfono non disponibile: controlla il permesso del browser.');
      return;
    }
    pezzi.current = [];
    const rec = new MediaRecorder(stream.current);
    rec.ondataavailable = (e) => { if (e.data.size > 0) pezzi.current.push(e.data); };
    rec.onstop = () => {
      stream.current?.getTracks().forEach((t) => t.stop());
      const blob = new Blob(pezzi.current, { type: rec.mimeType || 'audio/webm' });
      const durata = Math.max(1, Math.round((Date.now() - inizio.current) / 1000));
      setAnteprima({ blob, url: URL.createObjectURL(blob), secondi: durata });
      setStato('anteprima');
    };
    recorder.current = rec;
    inizio.current = Date.now();
    rec.start(1000);
    setSecondi(0);
    setStato('registra');
    timer.current = setInterval(() => {
      const s = Math.round((Date.now() - inizio.current) / 1000);
      setSecondi(s);
      if (s >= MAX_SECONDI) ferma();
    }, 500);
  };

  const ferma = () => {
    clearInterval(timer.current);
    if (recorder.current && recorder.current.state !== 'inactive') recorder.current.stop();
  };

  const scarta = () => {
    setAnteprima(null);
    setStato('fermo');
  };

  const tieni = () => {
    const tipo = anteprima.blob.type || 'audio/webm';
    const est = tipo.includes('mp4') ? 'm4a' : tipo.includes('ogg') ? 'ogg' : 'webm';
    onPronto(anteprima.blob, `messaggio-vocale.${est}`, anteprima.secondi);
    setAnteprima(null);
    setStato('fermo');
  };

  return (
    <div style={s.box}>
      {stato === 'fermo' && (
        <button type="button" style={s.btnRegistra} onClick={avvia} disabled={disabilitato}>
          <Mic size={16} /> Registra un messaggio vocale
        </button>
      )}
      {stato === 'registra' && (
        <div style={s.riga}>
          <span style={s.puntino} />
          <span style={s.tempo}>{fmt(secondi)} / {fmt(MAX_SECONDI)}</span>
          <button type="button" style={s.btnStop} onClick={ferma}>
            <Square size={14} /> Ferma
          </button>
        </div>
      )}
      {stato === 'anteprima' && anteprima && (
        <div style={s.riga}>
          <audio controls src={anteprima.url} style={{ height: '36px', maxWidth: '100%' }} />
          <button type="button" style={s.btnTieni} onClick={tieni}><Check size={14} /> Allega</button>
          <button type="button" style={s.btnScarta} onClick={scarta} aria-label="Scarta"><Trash2 size={15} /></button>
        </div>
      )}
      {errore && <div style={s.errore}>{errore}</div>}
    </div>
  );
}

const s = {
  box: { display: 'flex', flexDirection: 'column', gap: '0.4rem' },
  riga: { display: 'flex', alignItems: 'center', gap: '0.6rem', flexWrap: 'wrap' },
  btnRegistra: { display: 'inline-flex', alignItems: 'center', gap: '0.45rem', padding: '0.55rem 0.95rem', backgroundColor: '#FEF2F2', color: '#B91C1C', border: '1px solid #FECACA', borderRadius: '10px', fontSize: '0.87rem', fontWeight: 600, cursor: 'pointer', minHeight: '40px', alignSelf: 'flex-start' },
  puntino: { width: '10px', height: '10px', borderRadius: '50%', backgroundColor: '#DC2626', animation: 'pulse 1s infinite' },
  tempo: { fontVariantNumeric: 'tabular-nums', fontWeight: 600, color: '#B91C1C' },
  btnStop: { display: 'inline-flex', alignItems: 'center', gap: '0.35rem', padding: '0.45rem 0.85rem', backgroundColor: '#DC2626', color: 'white', border: 'none', borderRadius: '8px', fontWeight: 600, cursor: 'pointer', minHeight: '36px' },
  btnTieni: { display: 'inline-flex', alignItems: 'center', gap: '0.35rem', padding: '0.45rem 0.85rem', backgroundColor: '#10B981', color: 'white', border: 'none', borderRadius: '8px', fontWeight: 600, cursor: 'pointer', minHeight: '36px' },
  btnScarta: { border: 'none', background: 'none', color: '#6B7280', cursor: 'pointer', display: 'inline-flex', padding: '0.3rem' },
  errore: { fontSize: '0.8rem', color: '#B91C1C' },
};
